import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Header from '../../components/ui/Header';
import DashboardMetrics from './components/DashboardMetrics';
import QuickActions from './components/QuickActions';
import ContentManagement from './components/ContentManagement';
import MediaUploadCenter from './components/MediaUploadCenter';
import UserInquiries from './components/UserInquiries';
import Icon from '../../components/AppIcon';
import { notificationService } from '../../services/notificationService';
import EmailTemplateEditor from './components/EmailTemplateEditor';
import EmailSequenceManager from './components/EmailSequenceManager';

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    // Request notification permission on dashboard load
    const checkNotificationPermission = async () => {
      const granted = await notificationService?.requestPermission();
      setNotificationsEnabled(granted);
    };
    checkNotificationPermission();
  }, []);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
    { id: 'content', label: 'Content', icon: 'FileText' },
    { id: 'media', label: 'Media', icon: 'Image' },
    { id: 'inquiries', label: 'Inquiries', icon: 'MessageSquare' },
    { id: 'email-templates', label: 'Email Templates', icon: 'Mail' },
    { id: 'email-sequences', label: 'Email Sequences', icon: 'Send' }
  ];

  const pageVariants = {
    initial: { opacity: 0 },
    animate: {
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      className="min-h-screen bg-background"
    >
      <Header />
      <div className="pt-24 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Page Header */}
          <div className="mb-6 sm:mb-8">
            <div className="flex items-start sm:items-center justify-between flex-col sm:flex-row gap-4">
              <div>
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground mb-2">
                  Admin Dashboard
                </h1>
                <p className="text-sm sm:text-base text-muted-foreground">
                  Manage your site content, media, and user interactions
                </p>
              </div>
              {notificationsEnabled && (
                <div className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-green-500/10 border border-green-500/20 rounded-lg">
                  <Icon name="Bell" size={18} className="text-green-500" />
                  <span className="text-xs sm:text-sm text-green-500 font-medium">Notifications Active</span>
                </div>
              )}
            </div>
          </div>

          {/* Mobile Tab Menu Toggle */}
          <div className="lg:hidden mb-4">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="w-full flex items-center justify-between px-4 py-3 bg-card border border-border rounded-lg text-foreground font-medium"
            >
              <span className="flex items-center gap-2">
                <Icon name={tabs?.find(t => t?.id === activeTab)?.icon} size={20} />
                {tabs?.find(t => t?.id === activeTab)?.label}
              </span>
              <Icon name={mobileMenuOpen ? "ChevronUp" : "ChevronDown"} size={20} />
            </button>
          </div>

          {/* Mobile Tab Dropdown */}
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="lg:hidden mb-4 bg-card border border-border rounded-lg overflow-hidden"
            >
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => {
                    setActiveTab(tab?.id);
                    setMobileMenuOpen(false);
                  }}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors border-b border-border last:border-b-0 ${
                    activeTab === tab?.id
                      ? 'bg-primary/10 text-primary' :'text-muted-foreground hover:bg-muted'
                  }`}
                >
                  <Icon name={tab?.icon} size={18} />
                  {tab?.label}
                </button>
              ))}
            </motion.div>
          )}

          {/* Desktop Tab Navigation */}
          <div className="hidden lg:block mb-8 border-b border-border">
            <div className="flex gap-2 overflow-x-auto">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center gap-2 px-6 py-3 text-sm font-medium transition-all duration-250 border-b-2 whitespace-nowrap ${
                    activeTab === tab?.id
                      ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-muted'
                  }`}
                >
                  <Icon name={tab?.icon} size={18} />
                  {tab?.label}
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="space-y-6">
            {activeTab === 'overview' && (
              <>
                <DashboardMetrics />
                <QuickActions onTabChange={setActiveTab} />
              </>
            )}
            {activeTab === 'content' && <ContentManagement />}
            {activeTab === 'media' && <MediaUploadCenter />}
            {activeTab === 'inquiries' && <UserInquiries />}
            {activeTab === 'email-templates' && <EmailTemplateEditor />}
            {activeTab === 'email-sequences' && <EmailSequenceManager />}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default AdminDashboard;